import { decodeDirectLinkResponse } from './direct-link-failure.js';
import { recordPlanPaymentReturn } from '#~/api/billing/payment-return.js';
import { config } from '#~/config/index.js';
import { logger } from '#~/logger/index.js';
import type { DiscoveredService, DiscoveryHealth } from '#~/agent/discovery/utils.js';
import { AUTH_LOGIN_FAILURE_EVENT_NAMES, EVENT_CATEGORIES } from '#~/analytics/constants/index.js';
import {
  createAuthJourneyTracker,
  shouldEmitAuthFailureEvent
} from '#~/analytics/domains/auth-journey.js';
import {
  SIGN_OUT_CAUSE,
  trackSignIn,
  trackSignOut,
  wasSignOutTracked
} from '#~/analytics/domains/sign-out-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import type { AnalyticsEvent, AuthJourneyStateTransport } from '#~/analytics/types/index.js';
import {
  beginAuthJourney,
  claimAuthJourneyTerminal,
  getActiveAuthJourney,
  setActiveAuthJourney
} from '#~/auth/index.js';
import { buildCollisionPath } from '#~/auth/collision.js';
import {
  getPermissionProbeRequestOutcome,
  getPermissionProbeStatus
} from '#~/test-probes/permissions-probe.js';
import { toIdFromHex } from '#~/api/utils.js';
import { authStore, beginSessionEnded } from '#~/auth/store.js';
import { readSessionReturn, writeSessionReturn } from '#~/auth/session-return.js';
import { clearInitialBillingReceipts } from '#~/api/agent/initial-billing.js';
import { bindBootAccount } from '#~/routes/root/boot-budget.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { OfflineError } from '#~/error/index.js';
import type { IPControllerInfo } from '#~/ipjoystick/index.js';
import { useTrayStore } from '#~/routes/electron/tray/store.js';
import type { Agent } from '@studio/models/agent';
import type { User } from '@studio/models/user';
import type { T_Maybe } from '@tryiris-ai/js-types';

import { isElectronApp } from './utils.js';
// ENG-4154: identity is owned by the renderer IdentityState store (single
// source of truth). The legacy `getBridgeLinkedEncoderID`/
// `primeBridgeLinkedEncoderID` API entries are now thin back-compat shims
// over the store — no per-consumer cache, no pRetry fan-out. Per rule:
// reference-rules-in-comments (ai.knowledge/global).
import {
  primeLinkedAgentId,
  resolveLinkedAgentId,
  resetSessionIdentity,
  type IdentityState
} from './identity-store.js';
import type { ViscaBridgeInquiryResponse } from '@tryiris-ai/common.protojs/joystick/v1/joystick';
import type { ProxyImpl } from '#~/agent/proxy.js';

// Module-level tracker — adapter from (name, category, props) → AnalyticsEvent,
// mirroring auth/provider.tsx so the desktop and web sign-in stages go through
// the same emit helper (ENG-4330).
const authJourneyTracker = createAuthJourneyTracker((name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
});
// ENG-4874: same adapter shape for the sign-out / sign-in pair.
const trackSignOutLifecycleEvent: AnalyticsTrackFn = (name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
};

// Store callbacks for mDNS service discovery
const mDNSServiceDiscoveryCallbacks: Set<(service: DiscoveredService) => void> = new Set();
const mDNSIrisServiceDiscoveryCallbacks: Set<(service: DiscoveredService) => void> = new Set();
const mDNSHealthCallbacks: Set<(health: DiscoveryHealth) => void> = new Set();

type ElectronLoginArgs = {
  type: 'email' | 'google';
  authJourney: AuthJourneyStateTransport;
  hostedAuthParams: Record<string, string>;
};

type ElectronRegisterArgs = {
  authJourney: AuthJourneyStateTransport;
  hostedAuthParams: Record<string, string>;
};

type ElectronLoginPayload = {
  userInfo: any;
  authJourney: AuthJourneyStateTransport | null;
};

type ElectronAuthErrorPayload = {
  errorMessage: string;
  authJourney: AuthJourneyStateTransport | null;
};

const resolveUserInfoId = (userInfo: any): string | undefined =>
  userInfo?.id ?? userInfo?.user_id ?? userInfo?.sub;

const normalizeLoginPayload = (payload: any): ElectronLoginPayload => {
  if (
    payload &&
    typeof payload === 'object' &&
    ('userInfo' in payload || 'authJourney' in payload)
  ) {
    return payload as ElectronLoginPayload;
  }

  return {
    userInfo: payload,
    authJourney: null
  };
};

const normalizeAuthErrorPayload = (payload: any): ElectronAuthErrorPayload => {
  if (payload && typeof payload === 'object' && 'errorMessage' in payload) {
    return payload as ElectronAuthErrorPayload;
  }

  return {
    errorMessage: payload instanceof Error ? payload.message : String(payload ?? 'Unknown error'),
    authJourney: null
  };
};

/**
 * Recover Auth0's error code and description from the main process's
 * auth-error message: `<error>: <description>`, or either half alone
 * (studio/electron/src/main/index.ts describeAuthErrorDeepLink). Only one
 * leading snake_case OAuth code is split off, so the collision parser still
 * reads Auth0's own description from its first character.
 */
const splitAuthErrorMessage = (message: string): { error?: string; errorDescription?: string } => {
  const composed = /^([a-z][a-z0-9_]*): ([\s\S]+)$/.exec(message);

  if (composed?.[1] && composed[2]) {
    return { error: composed[1], errorDescription: composed[2] };
  }

  return /^[a-z][a-z0-9_]*$/.test(message) ? { error: message } : { errorDescription: message };
};

/**
 * Normalize the main-process `bridge:get-current-identity` reply into an
 * {@link IdentityState}. Tolerates legacy/rolling-deploy main builds that do
 * not yet implement the handler (returns a deterministic `resolving` so the
 * store's bounded pull keeps trying rather than latching a wrong terminal).
 */
const normalizeIdentityReply = (reply: unknown): IdentityState => {
  if (reply && typeof reply === 'object' && 'status' in reply) {
    return reply as IdentityState;
  }
  return { status: 'resolving' };
};

/** Shape returned by main's `bridge:liveness` snapshot (ipc.ts). */
export interface BridgeLivenessSnapshot {
  /** The auth sidecar has written its port file — the process is up. */
  readonly authReady: boolean;
  /** The sidecar has answered main at least once this session. */
  readonly launcherReady: boolean;
  /**
   * The kor process manager is running, or main is still bringing it up
   * (verifying the install, restarting it after an exit). False once it has
   * exited, been signalled, or could not be spawned. Absent when main has no
   * process manager to ask (older main, embedded builds).
   */
  readonly korAlive?: boolean;
}

export const ElectronAPI = {
  getAccessToken: () => {
    return window.electron?.ipcRenderer?.invoke('get-access-token') as Promise<string>;
  },
  getUpdateStatus: () => window.electron?.ipcRenderer?.invoke('update:get-status'),
  snoozeUpdate: (version: string) =>
    window.electron?.ipcRenderer?.invoke('update:snooze', { version }),
  // `force` (ENG-3797 email-verification-gate poll): pass true to make the main
  // process bypass its cached userInfo and re-read Auth0 /userinfo live. A
  // custom-DB signup caches email_verified:false and a silent token refresh
  // never re-runs the login script, so the gate's poll must force a live read
  // to ever observe verification and auto-dismiss. Omitted/false keeps the
  // warm-cache fast path used by the boot/identity callers.
  getUserInfo: async (
    force?: boolean
  ): Promise<{
    data: any;
    // Session status from main process. Undefined on legacy / rolling-deploy
    // responses, which the renderer interprets as "fall back to polling."
    // See knowledge: electron-auth-bootstrap-race (ai.knowledge/platform) —
    // the pending branch preserves the Windows webContents.send fallback.
    status?: 'authenticated' | 'pending' | 'unauthenticated';
  }> => {
    const result = await window.electron?.ipcRenderer?.invoke('get-user-info', force);

    // Handle structured response with network error
    if (result && typeof result === 'object' && 'error' in result) {
      if (result.error === 'NETWORK_ERROR') {
        throw new OfflineError();
      }
      // Main returns these as `{ success: false, error }` so Electron does not
      // wrap the message. Throw the same string classifyRefreshFailure parses.
      if (result.error === 'userinfo 401' || result.error === 'userinfo 429') {
        throw new Error(result.error);
      }
    }

    // Handle structured response with data. If main-process W1 provides a
    // `status` discriminator, propagate it so the renderer can short-circuit
    // the poll on unauthenticated. Otherwise return data with status omitted —
    // renderer falls back to the pre-W2 polling behavior.
    // Per tsconfig `exactOptionalPropertyTypes`, the `status` key is added
    // only when present; never set to `undefined` explicitly.
    if (result && typeof result === 'object' && 'data' in result) {
      if (
        'status' in result &&
        (result.status === 'authenticated' ||
          result.status === 'pending' ||
          result.status === 'unauthenticated')
      ) {
        return {
          data: result.data,
          status: result.status as 'authenticated' | 'pending' | 'unauthenticated'
        };
      }
      return { data: result.data };
    }

    // Handle legacy response (just the data) - for backwards compatibility.
    // No status discriminator → renderer polls as it did pre-W2.
    return { data: result };
  },
  getOS: () => {
    return window.electron?.ipcRenderer?.invoke('get-os') as Promise<
      'mac' | 'windows' | 'linux' | 'unknown'
    >;
  },
  loaded: () => {
    if (!isElectronApp()) {
      return true;
    }

    return window.electron?.ipcRenderer?.invoke('studio:loaded');
  },
  logLiveKitDiagnostic: (event: unknown): Promise<boolean> => {
    const invoke = window.electron?.ipcRenderer?.invoke;
    if (!invoke) {
      return Promise.resolve(false);
    }
    return invoke('diagnostics:livekit-log', event) as Promise<boolean>;
  },
  setPostHogErrorTrackingVerbose: (enabled: boolean): boolean => {
    const send = window.electron?.ipcRenderer?.send;
    if (!send) {
      return false;
    }
    // Main-process capture cannot read PostHog flags directly, so renderer relays state.
    send('posthog:set-error-tracking-verbose', enabled);
    return true;
  },
  trayResize: ({ width, height }: { width?: number; height?: number }) => {
    return window.electron?.ipcRenderer?.invoke('tray:resize', { width, height });
  },
  trayOpen: () => {
    return window.electron?.ipcRenderer?.invoke('tray:open');
  },
  trayClose: () => {
    return window.electron?.ipcRenderer?.invoke('tray:close');
  },
  trayVisibility: (): Promise<boolean> => {
    return window.electron?.ipcRenderer?.invoke('tray:visibility');
  },
  openStudio: () => {
    return window.electron?.ipcRenderer?.invoke('studio:open');
  },
  onReloadStudio: () => {
    // this ipc renderer command doesn't seem to exist on the electron side-of-things
    return window.electron?.ipcRenderer?.invoke('studio:restart');
  },
  // Zero-network liveness snapshot of the local bridge from main
  // (`bridge:liveness`). Never touches the sidecar, so a boot watchdog can
  // poll it to tell "still starting" from "wedged" without the probe itself
  // stalling on a slow bridge. Resolves undefined outside Electron.
  bridgeLiveness: (): Promise<BridgeLivenessSnapshot | undefined> => {
    const invoke = window.electron?.ipcRenderer?.invoke;
    if (!invoke) {
      return Promise.resolve(undefined);
    }
    return invoke('bridge:liveness') as Promise<BridgeLivenessSnapshot | undefined>;
  },
  restart: () => {
    return window.electron?.ipcRenderer?.invoke('bridge:reboot');
  },
  logout: () => {
    return window.electron?.ipcRenderer?.send('logout');
  },
  login: ({ type, authJourney, hostedAuthParams }: ElectronLoginArgs) => {
    return window.electron?.ipcRenderer?.send('login', { type, authJourney, hostedAuthParams });
  },
  register({ authJourney, hostedAuthParams }: ElectronRegisterArgs) {
    return window.electron?.ipcRenderer?.send('register', { authJourney, hostedAuthParams });
  },
  openUrl: (url: string, newWindow: boolean = false) => {
    if (url === undefined || url === '') {
      return;
    }

    if (!isElectronApp()) {
      if (newWindow) {
        window.open(url, '_self');
      } else {
        window.location.href = url;
      }
      return;
    }

    return window.electron?.ipcRenderer?.send('open-url', url);
  },
  sendLinkRequest: () => {
    return window.electron?.ipcRenderer?.invoke('bridge:link');
  },
  sendLinkRequestV2: async (deadlineAt?: number): Promise<T_Maybe<Agent['id']>> => {
    // Only callers with a bounded readiness contract opt in. The legacy Auto
    // Link controller owns a different startup budget and keeps its old call.
    return deadlineAt === undefined
      ? window.electron?.ipcRenderer?.invoke('bridge:linkv2')
      : window.electron?.ipcRenderer?.invoke('bridge:linkv2', deadlineAt);
  },
  /**
   * FC-1.5 (Hive manual local-bridge link): the MANUAL link screen's sidecar
   * entry point. Distinct from sendLinkRequestV2 — `bridge:linkv2` is the
   * AUTO Link controller's channel and is vendor-denied on Hive at the
   * Electron-main boundary (bypass protection, FC-3.5); this channel carries
   * the explicit user-driven manual flow, allowed for both vendors. Resolves
   * the sidecar's linkUrl (same payload shape as v2).
   * Per rule: reference-rules-in-comments (ai.knowledge/global).
   */
  sendLinkRequestManual: async (): Promise<T_Maybe<Agent['id']>> => {
    return window.electron?.ipcRenderer?.invoke('bridge:link-manual');
  },
  /**
   * ENG-4154 (warm account-switch): ask the sidecar to unlink its agent and
   * wipe its stored credentials. Main-side this also requests an app reboot
   * (IRIS_REBOOT_TOKEN) so the encoder/resolver reconverge through the
   * fresh-install link path. Resolves `true` only when the sidecar confirmed
   * the unlink round-trip. Called by the auto-link controller when the
   * current account's registry definitively disowns a sidecar-claimed link.
   */
  sendUnlinkRequest: async (): Promise<boolean> => {
    return (await window.electron?.ipcRenderer?.invoke('bridge:unlink')) === true;
  },
  hasBridgeDatabase: async (): Promise<boolean> => {
    try {
      return (await window.electron?.ipcRenderer?.invoke('bridge:has-database')) ?? false;
    } catch {
      return false;
    }
  },

  /**
   * Pull/replay primitive (W1 §5). Reads the main-process resolver's CURRENT
   * IdentityState. The renderer IdentityState store calls this on init and as
   * its bounded pull fallback (push-drop safe on Windows). `bridge:get-id`
   * stays UNCHANGED — this is an additive channel.
   */
  getCurrentIdentityState: async (): Promise<IdentityState> => {
    if (!isElectronApp()) {
      return { status: 'unlinked' };
    }
    try {
      const reply = await window.electron?.ipcRenderer?.invoke('bridge:get-current-identity');
      return normalizeIdentityReply(reply);
    } catch (error) {
      console.warn('getCurrentIdentityState invoke failed', error);
      return { status: 'resolving' };
    }
  },

  /**
   * @deprecated Back-compat shim — identity now lives in the IdentityState
   * store. New code should import `primeLinkedAgentId` from
   * `#~/electron/identity-store.js`.
   */
  primeBridgeLinkedEncoderID: (agentId: T_Maybe<Agent['id']>) => {
    primeLinkedAgentId(agentId);
  },
  /**
   * @deprecated Back-compat shim over the IdentityState store. New code should
   * import `resolveLinkedAgentId` from `#~/electron/identity-store.js`. No
   * per-consumer cache or pRetry here anymore — the store owns the single
   * pull + push subscription.
   */
  getBridgeLinkedEncoderID: async (force = false): Promise<T_Maybe<Agent['id']>> => {
    return resolveLinkedAgentId(force);
  },
  getBridgeEnvironment: () => {
    return window.electron?.ipcRenderer?.invoke('bridge:get-env');
  },

  getRecordingPath: () => {
    return window.electron?.ipcRenderer?.invoke('recording:path');
  },

  openRecordingPath: () => {
    window.electron?.ipcRenderer?.invoke('recording:open-path');
  },

  selectRecordingDirectory: () => {
    const selectedPath = window.electron?.ipcRenderer?.invoke('recording:select-directory');
    return selectedPath;
  },

  appVersion: () => {
    return window.electron?.ipcRenderer?.invoke('app:version');
  },
  getSystemInfo: () => {
    return window.electron?.ipcRenderer?.invoke('system:info');
  },
  /** The name this machine goes by; see studio/electron utils/machine-name.ts. */
  getMachineNames: (): Promise<{ friendlyName: string } | undefined> => {
    return window.electron?.ipcRenderer?.invoke('system:machine-name');
  },

  permissions: {
    // ENG-4415: mediaStatus/networkStatus/requestMedia/requestNetwork all
    // consult the ENG-4415 permissions-probe before touching the real IPC —
    // see that module's header for why (native OS TCC dialogs are not
    // Playwright-drivable). getPermissionProbeStatus/RequestOutcome return
    // `undefined` outside e2e, so these fall through to the real channel
    // unchanged for every real user and every other e2e spec.
    mediaStatus: (media: 'camera' | 'microphone'): Promise<boolean> => {
      const probed = getPermissionProbeStatus(media);
      if (probed !== undefined) {
        return Promise.resolve(probed);
      }
      return window.electron?.ipcRenderer?.invoke('app:media-permission-status', media);
    },
    networkStatus: (): Promise<boolean> => {
      const probed = getPermissionProbeStatus('network');
      if (probed !== undefined) {
        return Promise.resolve(probed);
      }
      return window.electron?.ipcRenderer?.invoke('app:network-permission-status');
    },
    startupStatus: (): Promise<boolean> => {
      return window.electron?.ipcRenderer?.invoke('app:get-startup');
    },

    requestMedia: (media: 'camera' | 'microphone'): Promise<boolean> => {
      const probed = getPermissionProbeRequestOutcome(media);
      if (probed !== undefined) {
        return Promise.resolve(probed);
      }
      return window.electron?.ipcRenderer?.invoke('app:media-permission-request', media);
    },
    requestNetwork: (): Promise<boolean> => {
      const probed = getPermissionProbeRequestOutcome('network');
      if (probed !== undefined) {
        return Promise.resolve(probed);
      }
      return window.electron?.ipcRenderer?.invoke('app:network-permission-request');
    },
    requestStartup: (value: boolean) => {
      return window.electron?.ipcRenderer?.invoke('app:set-startup', value);
    },
    openSettingsCamera: () => {
      return window.electron?.ipcRenderer?.invoke('open-settings-camera');
    },
    openSettingsMicrophone: () => {
      return window.electron?.ipcRenderer?.invoke('open-settings-microphone');
    }
  },
  getIPPort: () => {
    return window.electron?.ipcRenderer?.invoke('joystick:get-ip');
  },

  launchViscaBridge: (context: IPControllerInfo) => {
    return window.electron?.ipcRenderer?.invoke('joystick:launch-bridge', context);
  },

  stopViscaBridge: (port: number) => {
    return window.electron?.ipcRenderer?.invoke('joystick:stop-bridge', port);
  },

  respondViscaBridge: (port: number, resp: ViscaBridgeInquiryResponse) => {
    window.electron?.ipcRenderer?.send('joystick:response', port, resp);
  },

  listViscaBridges: (): Promise<IPControllerInfo[]> => {
    return window.electron?.ipcRenderer?.invoke('joystick:list');
  },

  // ENG-3756 readiness handshake: renderer awaits Electron main's
  // ViscaBridgeManager.readyPromise before any list/restore call. Per the
  // spec's constants block, main always resolves (never rejects, never
  // hangs); renderer guards with a shorter timeout in comm.ts.
  waitForJoystickReady: (): Promise<{ ok: boolean; error?: string }> => {
    return (
      window.electron?.ipcRenderer?.invoke('joystick:wait-ready') ?? Promise.resolve({ ok: false })
    );
  },

  prepareLocalBridge: (uris: string[], caCertificate: Uint8Array) => {
    return window.electron?.ipcRenderer?.invoke('prepare-local-bridge', { uris, caCertificate });
  },

  generateLocalTokenAndGetURIs: (
    identity: string,
    room: string
  ): Promise<{
    token: string;
    uris: string[];
    caCertificate: Uint8Array;
  }> => {
    return window.electron?.ipcRenderer?.invoke('bridge:local-auth-token', {
      identity,
      room: toIdFromHex(room)!,
      roomJoin: true
    });
  },
  mDNS: {
    /**
     * Query for services
     *
     * @param service - The service to query for
     * @returns The services discovered
     */
    query: (service: string) => {
      return window.electron?.ipcRenderer?.invoke('mdns:query', service);
    },
    /**
     * Query for *iris* services
     *
     * @param service - The service to query for
     * @returns The services discovered
     */
    queryIris: () => {
      return window.electron?.ipcRenderer?.invoke('mdns:iris:query');
    },
    /**
     * Get all discovered services
     *
     * @returns The services discovered
     */
    getDiscovered: () => {
      return window.electron?.ipcRenderer?.invoke('mdns:getDiscovered');
    },
    /**
     * Get all *iris* discovered services
     *
     * @returns The services discovered
     */
    getIrisDiscovered: () => {
      return window.electron?.ipcRenderer?.invoke('mdns:iris:getDiscovered');
    },
    /**
     * Get all extended-discovery devices (non-Iris mDNS devices found across
     * the broadcast service types: _http, _https, _ssh, _onvif, _ndi, etc.)
     * Used for fingerprint matching by the encoder.
     */
    getDiscoveredDevices: () => {
      return window.electron?.ipcRenderer?.invoke('mdns:getDiscoveredDevices');
    },
    // @remove tirus for testing.
    getIrisTestDiscovered: () => {
      return window.electron?.ipcRenderer?.invoke('mdns:iris:testingData');
    },
    /**
     * Listen for *general* service discovery events continuously.
     *
     * @param callback - Function to call when a new service is discovered
     * @returns Function to remove the listener
     */
    onServiceDiscovered: (callback: (service: DiscoveredService) => void) => {
      mDNSServiceDiscoveryCallbacks.add(callback);

      // Return a function to remove the listener
      return () => {
        mDNSServiceDiscoveryCallbacks.delete(callback);
      };
    },
    /**
     * Listen for iris *specific* service discovery events continuously.
     *
     * @param callback - Function to call when a new service is discovered
     * @returns Function to remove the listener
     */
    onIrisServiceDiscovered: (callback: (service: DiscoveredService) => void) => {
      mDNSIrisServiceDiscoveryCallbacks.add(callback);

      // Return a function to remove the listener
      return () => {
        mDNSIrisServiceDiscoveryCallbacks.delete(callback);
      };
    },
    /**
     * Read the current health of each way of finding cameras (mDNS and the
     * network scan), for add-camera to check when it opens.
     *
     * @returns Per-method ok/failed state, or undefined outside Electron
     */
    getHealth: async (): Promise<DiscoveryHealth | undefined> => {
      return window.electron?.ipcRenderer?.invoke('mdns:getHealth');
    },
    /**
     * Listen for discovery health changes continuously. Main pushes a full
     * snapshot on every transition, including recovery.
     *
     * @param callback - Function to call with the new health
     * @returns Function to remove the listener
     */
    onHealthChanged: (callback: (health: DiscoveryHealth) => void) => {
      mDNSHealthCallbacks.add(callback);

      // Return a function to remove the listener
      return () => {
        mDNSHealthCallbacks.delete(callback);
      };
    },
    /**
     * Proxy a direct link status AND request to the agent.
     *
     * @param service - The service to proxy the request to
     * @param username - The username to use for the request
     * @param password - The password to use for the request
     *
     * @returns The response indicating the status and request information from the agent
     */
    proxyDirectStatusAndLinkRequest: async ({
      service,
      bridgeLinkingRequestId,
      isPreApprovedForUnlink,
      username,
      password,
      user,
      studioName
    }: {
      service: DiscoveredService | T_Maybe<Discovery>;
      bridgeLinkingRequestId?: string;
      isPreApprovedForUnlink?: boolean;
      username?: string;
      password?: string;
      user?: Pick<User, 'email' | 'firstName' | 'lastName' | 'id'>;
      studioName?: string;
    }) => {
      return decodeDirectLinkResponse(
        await window.electron?.ipcRenderer?.invoke('proxy-direct-link-status-request', {
          service,
          bridgeLinkingRequestId,
          isPreApprovedForUnlink,
          username,
          password,
          user,
          studioName
        })
      );
    },
    denyLinkRequest: () => {
      return window.electron?.ipcRenderer?.invoke('proxy-deny-link-bridge-request');
    },
    cancelLinkRequest: (service: DiscoveredService) => {
      return window.electron?.ipcRenderer?.invoke('proxy-cancel-link-bridge-request', service);
    },
    approveLinkRequest: () => {
      return window.electron?.ipcRenderer?.invoke('proxy-approve-link-bridge-request');
    }
  }
};

/**
 * Leave the current session's Studio and start the document over at entry.
 * ENG-4154 Phase 4: tear down session identity so the next account starts from
 * `resolving` with a clean store/org/linked-room — kills the null-org
 * corruption from a previous account leaking across sessions.
 */
const restartStudioEntry = (): void => {
  resetSessionIdentity(undefined);
  // Initial-billing receipts are room-keyed sessionStorage and survive this reload.
  // Every desktop sign-out and cross-account sign-in passes here, so the next
  // account cannot read the previous account's unfinished setup as its own.
  clearInitialBillingReceipts();
  window.location.href = window.location.origin;
};

export const onLogout = () => {
  // ITG QA V2: sign-out ends the launch's account. Main keeps the launch clock
  // across this reload, and on Electron ProfileSynchronizer never binds null
  // (signed out reads as loading there). Left bound, signing back in as the
  // same account was a no-op bind onto the already-settled attempt, so Root
  // captured a dead signal and sat on "Loading your studio…". Unbinding starts
  // a fresh attempt that the next sign-in, any account, binds onto.
  // Per rule: settle-every-terminal-branch (ai.knowledge/global).
  bindBootAccount(null);
  authStore.setState({ userInfo: null });
  restartStudioEntry();
};

/** Preserve N8's destination before the existing desktop session teardown. */
export const onSessionEnded = (userId: string): void => {
  beginSessionEnded(userId, true);
  trackSignOut(trackSignOutLifecycleEvent, { cause: SIGN_OUT_CAUSE.SESSION_ENDED });
  onLogout();
};

const autoTrackingReadyCallbacks: (() => void)[] = [];
let isAutoTrackingReady = false;

export let autoTrackingLoading = false;

export const onAutoTrackingReady = (callback: () => void) => {
  if (isAutoTrackingReady) {
    callback();
    autoTrackingLoading = false;
    return;
  }

  autoTrackingLoading = true;
  autoTrackingReadyCallbacks.push(callback);
  window.electron?.ipcRenderer?.invoke('autotracking:start');
};

const subscribedProxies: ProxyImpl[] = [];

export const addProxySubscriber = (proxy: ProxyImpl) => {
  if (subscribedProxies.includes(proxy)) {
    return;
  }

  subscribedProxies.push(proxy);

  const notifiedReady: Record<string, number> = {};

  setInterval(() => {
    const workers = proxy?.state.control?.studio?.autoTrackingWorker ?? {};
    if (Object.keys(workers)?.length === 0) {
      return;
    }

    const key = Object.keys(workers)[0]!;
    const worker = workers[key]!;

    const isReady = worker?.state?.status?.readyAt ?? 0 > 0;

    if (isReady && notifiedReady[key] !== worker?.state?.status?.readyAt) {
      notifiedReady[key] = worker?.state?.status?.readyAt!;
      window.electron?.ipcRenderer?.send('autotracking:ready');
    }
  }, 1 * 1000);
};

if (isElectronApp()) {
  window.api = ElectronAPI;

  window.electron?.ipcRenderer?.on('login', (_ev: any, payload: any) => {
    const normalizedPayload = normalizeLoginPayload(payload);
    const previousUserInfo = authStore.getState().userInfo;
    const previousUserId = resolveUserInfoId(previousUserInfo);
    const nextUserId = resolveUserInfoId(normalizedPayload.userInfo);
    const resolvedJourney = normalizedPayload.authJourney ?? getActiveAuthJourney() ?? null;

    authStore.setState({ userInfo: normalizedPayload.userInfo ?? null });

    if (resolvedJourney) {
      setActiveAuthJourney(resolvedJourney);

      // Close the attempt. The browser tab that started this sign-in is still
      // open behind the app and its "Open Iris" button re-delivers the SAME
      // authorization code; Auth0 answers a consumed code with invalid_grant,
      // which arrives on `auth:error` below. Without this claim a sign-in that
      // worked would also be counted as one that failed.
      //
      // Only a payload that actually carries a user closes the attempt. The
      // Windows tray's "Sign In" item sends a BARE `login` with no payload
      // (main/tray/windows-tray.ts) purely to surface the sign-in screen, and
      // normalizeLoginPayload turns that into userInfo: undefined. Claiming on
      // it would mark an attempt that is still in flight as succeeded, so the
      // real failure arriving moments later would find the journey already
      // claimed and emit nothing — silently uncounting exactly the failure this
      // handler exists to count.
      if (normalizedPayload.userInfo !== undefined && normalizedPayload.userInfo !== null) {
        claimAuthJourneyTerminal(resolvedJourney.authJourneyId, 'succeeded');
      }
    }

    const shouldTrackLoginSuccess =
      Boolean(normalizedPayload.userInfo) &&
      (!previousUserInfo || (nextUserId !== undefined && nextUserId !== previousUserId));

    if (shouldTrackLoginSuccess && resolvedJourney) {
      // ENG-4330: shared with the web Auth0 redirect callback
      // (auth/provider.tsx) so both surfaces emit an identical property shape.
      authJourneyTracker.trackLoginSucceeded(resolvedJourney);
    }

    if (shouldTrackLoginSuccess) {
      // ENG-4874: the sign-in half of the sign-out pair. Not gated on a
      // journey: the pairing is by install, not by funnel attempt.
      trackSignIn(trackSignOutLifecycleEvent);
      const resume = readSessionReturn();
      writeSessionReturn(undefined);
      authStore.setState({ sessionEnded: undefined });
      // The entry reload cleared the old account. Only the same account may
      // resume its previous studio; another account starts at the normal entry.
      if (resume?.phase === 'signing_in' && resume.userId === nextUserId) {
        window.location.replace(resume.returnTo);
        return;
      }
    }

    // ITG QA V2: a sign-in link for another account opened while signed in
    // swaps the user without a sign-out. The open route (often the previous
    // account's Studio) stays mounted, Root never runs for the new account and
    // the boot sat on "Loading your studio…". Restart at entry, exactly as a
    // sign-out does, so the new account boots like any other sign-in.
    if (previousUserId !== undefined && nextUserId !== undefined && nextUserId !== previousUserId) {
      restartStudioEntry();
    }
  });

  window.electron?.ipcRenderer?.on('auth:error', (_ev: any, payload: any) => {
    const normalizedPayload = normalizeAuthErrorPayload(payload);
    // Main always sends authJourney: null. A leftover attempt (login screen
    // still open) is the usual path. A fresh window has none — that is how
    // the OS launches the app with the failure link. Begin an attempt so
    // that failure is still counted instead of dropped.
    // Per rule: settle-every-terminal-branch (ai.knowledge/global) — a
    //   failure the app already observed must still emit.
    const resolvedJourney =
      normalizedPayload.authJourney ?? getActiveAuthJourney() ?? beginAuthJourney('studio_login');

    // ENG-3451: account collision in the hosted-auth error handoff. Detected
    // BEFORE the terminal-claim gate below: the collision page is a UX
    // surface, not an analytics event, so it must route even when the
    // journey's terminal outcome was already claimed. Only the verified
    // COLLISION:<connection> prefix yields a path; every other error keeps
    // today's behavior exactly. Main reports Auth0's deny as
    // `<error>: <description>`, so split it before the prefix check.
    const collisionPath = buildCollisionPath(splitAuthErrorMessage(normalizedPayload.errorMessage));

    // One attempt, one countable failure. The OS can deliver an attempt's
    // outcome more than once -- the web callback auto-opens the deep link and
    // the "Open Iris" button re-opens the same url -- and a re-delivered
    // consumed code arrives here as invalid_grant even though the sign-in
    // succeeded. The journey id is the attempt id, so the first terminal
    // outcome per journey is the one that counts; a genuine retry begins a new
    // journey and still emits. This claim gates only the analytics emit — the
    // collision navigation below stays outside it (see ENG-3451 note above).
    if (claimAuthJourneyTerminal(resolvedJourney.authJourneyId, 'failed')) {
      setActiveAuthJourney(resolvedJourney);

      // ENG-4348 stage 3: dual-emits auth_login_failed + legacy oauth_error.
      // This site emits through track() directly (no AnalyticsTrackFn in scope),
      // so it iterates the same shared tuple the trackAuthLoginFailed helper
      // uses — keeping the pair in lockstep with the browser call sites.
      for (const name of AUTH_LOGIN_FAILURE_EVENT_NAMES) {
        const properties = {
          auth_journey_id: resolvedJourney.authJourneyId,
          source: resolvedJourney.source,
          surface: 'electron_login',
          error_message: normalizedPayload.errorMessage
        };

        // ENG-4416: same debounce-config gate the browser path applies via
        // shouldEmitAuthFailureEvent, so both emit sites honor one contract.
        if (!shouldEmitAuthFailureEvent(name, properties)) {
          continue;
        }

        const authFailureEvent: AnalyticsEvent = {
          category: EVENT_CATEGORIES.AUTH,
          name,
          properties
        };

        void trackAnalytics(authFailureEvent);
      }
    }

    if (collisionPath) {
      // Full navigation — the same pattern as the window:shutting-down /
      // window:restarting handlers below. The renderer is parked on
      // /electron/login waiting on the hosted flow; the collision page
      // replaces that dead end. Navigation comes AFTER the analytics emit so
      // the dual-emit is queued before unload (posthog flushes queued events
      // via its pagehide beacon).
      window.location.href = collisionPath;
    }
  });

  window.electron?.ipcRenderer?.on('logout', () => {
    // ENG-4874: main signs this document out on its own only when a token
    // refresh failed fatally (main/ipc.ts auth:refresh-auth-token and
    // auth.ts getAccessToken both call Auth.logout()). A sign-out this
    // document asked for was already recorded by routes/logout/logout.tsx or
    // auth/use-auth0.ts before it reached main, and a document with no
    // signed-in user has no session to have ended.
    if (!wasSignOutTracked() && authStore.getState().userInfo) {
      const userId = resolveUserInfoId(authStore.getState().userInfo);
      if (userId) {
        onSessionEnded(userId);
        return;
      }
      trackSignOut(trackSignOutLifecycleEvent, { cause: SIGN_OUT_CAUSE.SESSION_ENDED });
    }
    onLogout();
  });

  window.electron?.ipcRenderer?.on('tray:visibility-change', (_ev, show: boolean) => {
    useTrayStore.setState({ show });
  });

  // Dynamic import avoids api.ts → livekit → electron/index.ts → api.ts.
  window.electron?.ipcRenderer?.on('power:resume', (_ev, payload) => {
    void import('#~/livekit/broker.js').then(({ broker }) => {
      broker.onSystemResume(payload);
    });
  });

  let updaterManager: import('./updater.js').ElectronUpdateManager | undefined;
  void import('./updater.js')
    .then(({ ElectronUpdateManager }) => {
      updaterManager = new ElectronUpdateManager();
      // Acquire immediately. acquireAgent already polls resolveLinkedAgentId and
      // the broker every 250ms until the linked proxy appears, so a fixed sleep
      // here bought nothing and only widened the window after every renderer load
      // where update events are queued instead of written.
      void updaterManager.acquireAgent();
    })
    .catch((error) => {
      console.error('Failed to load electron updater manager', error);
    });
  window.electron?.ipcRenderer?.on('update:unavailable', (_event, payload) => {
    updaterManager?.handleUnavailable(payload);
  });

  window.electron?.ipcRenderer?.on('update:check-failed', () => {
    updaterManager?.handleCheckFailed();
  });

  window.electron?.ipcRenderer?.on('update:check-ok', () => {
    updaterManager?.handleCheckOk();
  });

  window.electron?.ipcRenderer?.on('update:available', (event: any, payload: any) => {
    ((window ?? {}) as any).lastUpdateAvailable = { event, payload };
    void updaterManager?.handleUpdateAvailable(payload);
  });

  window.electron?.ipcRenderer?.on('update:downloaded', (event: any, payload: any) => {
    ((window ?? {}) as any).lastDownloadedUpdate = { event, payload };
    void updaterManager?.handleUpdateDownloaded(payload);
  });

  window.electron?.ipcRenderer?.on('update:download-progress', (event: any, payload: any) => {
    ((window ?? {}) as any).lastDownloadProgress = { event, payload };
    void updaterManager?.handleUpdateDownloadProgress(payload);
  });

  window.electron?.ipcRenderer?.on('update:status', (_event: any, payload: any) => {
    updaterManager?.handleStatus(payload);
  });

  window.electron?.ipcRenderer?.on('request-login-status', () => {
    const state = authStore.getState();

    window.electron?.ipcRenderer?.send('login-status-response', state.userInfo);
  });

  window.electron?.ipcRenderer?.invoke('update:check-for-updates');

  window.electron?.ipcRenderer?.on('autotracking:ready', () => {
    isAutoTrackingReady = true;

    autoTrackingReadyCallbacks.forEach((callback) => callback());
  });

  window.electron?.ipcRenderer?.on(
    'billing-plan-update',
    (_, { roomID, checkoutSessionId }: { roomID: string; checkoutSessionId?: string }) => {
      const vendor = config.vendor;
      if (vendor === 'iris' || vendor === 'ptzoptics') {
        recordPlanPaymentReturn(vendor, roomID, checkoutSessionId ?? '');
      }
    }
  );

  /**
   * Listen for new services
   *
   * @param callback - The callback to call when a new service is discovered
   */
  window.electron?.ipcRenderer?.on('mdns:serviceDiscovered', (_event, service) => {
    // Notify all registered callbacks about the discovered service
    mDNSServiceDiscoveryCallbacks.forEach((callback) => {
      try {
        // console.log('___invoking the generic callback');
        callback(service);
      } catch (error) {
        console.error('Error in mDNS service discovery callback:', error);
      }
    });
  });

  /**
   * Listen for new *iris* services
   *
   * @param callback - The callback to call when a new iris service is discovered
   */
  window.electron?.ipcRenderer?.on('mdns:iris:serviceDiscovered', (_event, service) => {
    mDNSIrisServiceDiscoveryCallbacks.forEach((callback) => {
      try {
        // console.log('___invoking all iris callbacks');
        callback(service);
      } catch (error) {
        console.error('Error in mDNS service discovery callback:', error);
      }
    });
  });

  /**
   * Listen for discovery health changes (ENG-4948)
   *
   * @param health - Per-method ok/failed snapshot from main
   */
  window.electron?.ipcRenderer?.on('mdns:health', (_event, health: DiscoveryHealth) => {
    mDNSHealthCallbacks.forEach((callback) => {
      try {
        callback(health);
      } catch (error) {
        logger.error('Error in mDNS health callback:', error);
      }
    });
  });

  /**
   * Listen for link request notification alerts
   *
   * @param data - The data to listen for
   */
  window.electron?.ipcRenderer?.on('link-request-notification-alert', (_event, data) => {
    // Emit a custom event that components can listen to
    window.dispatchEvent(
      new CustomEvent('bridge:link-request-notification-alert', { detail: data })
    );
  });

  /**
   * Listen for link request cancel events
   */
  window.electron?.ipcRenderer?.on('link-request-cancel', (_event, _data) => {
    window.dispatchEvent(new CustomEvent('bridge:link-request-cancel'));
  });
}
